import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, FileText, Sparkles, Plus, Trash2, 
  Clock, CheckCircle, AlertCircle, Loader2, 
  ClipboardList, Eye, Download
} from "lucide-react";

export const SOPGeneratorPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sops, setSOPs] = useState([]);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [formData, setFormData] = useState({
    process_name: "",
    department: "",
    description: "",
    objectives: "",
    audience: "",
    context: ""
  });

  useEffect(() => {
    fetchSOPs();
  }, []);

  const fetchSOPs = async () => {
    try {
      const response = await authFetch(`${API}/sops`, {  });
      if (response.ok) {
        const data = await response.json();
        setSOPs(data);
      }
    } catch (_error) {
      console.error("Error fetching SOPs:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.process_name || !formData.description) {
      toast.error("Process name and description are required");
      return;
    }

    setGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/generate-sop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("SOP generated successfully!");
        setSelectedSOP(data.sop);
        fetchSOPs();
        setFormData({
          process_name: "",
          department: "",
          description: "",
          objectives: "",
          audience: "",
          context: ""
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate SOP");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (sopId) => {
    if (!confirm("Delete this SOP?")) return;
    
    try {
      const response = await authFetch(`${API}/sops/${sopId}`, {
        method: "DELETE",
        
      });
      if (response.ok) {
        toast.success("SOP deleted");
        fetchSOPs();
        if (selectedSOP?.sop_id === sopId) setSelectedSOP(null);
      }
    } catch (_error) {
      toast.error("Failed to delete SOP");
    }
  };

  const viewSOP = async (sopId) => {
    try {
      const response = await authFetch(`${API}/sops/${sopId}`, {  });
      if (response.ok) {
        const data = await response.json();
        setSelectedSOP(data.generated_sop);
      }
    } catch (_error) {
      toast.error("Failed to load SOP");
    }
  };

  const printSOP = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="sop-generator-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10 print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">SOP Generator</h1>
                <p className="text-sm text-neutral-500">Create Standard Operating Procedures with AI</p>
              </div>
            </div>
            {selectedSOP && (
              <Button onClick={printSOP} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Print/Export
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Explanation Card */}
        <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-sky-50 mb-6">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">AI Standard Operating Procedure (SOP) Generator</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Document your business processes with professional SOPs. Describe any procedure—from client 
                  onboarding to daily operations—and AI will generate detailed step-by-step instructions, 
                  checklists, and best practices. Build a library of SOPs to train team members and maintain consistency.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-cyan-200 text-cyan-700">Step-by-Step Guide</span>
                  <span className="px-2 py-1 bg-white rounded border border-cyan-200 text-cyan-700">Checklists</span>
                  <span className="px-2 py-1 bg-white rounded border border-cyan-200 text-cyan-700">Best Practices</span>
                  <span className="px-2 py-1 bg-white rounded border border-cyan-200 text-cyan-700">PDF Export</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="create" className="space-y-6">
          <TabsList className="print:hidden">
            <TabsTrigger value="create">Create New</TabsTrigger>
            <TabsTrigger value="library">My SOPs ({sops.length})</TabsTrigger>
            {selectedSOP && <TabsTrigger value="view">View SOP</TabsTrigger>}
          </TabsList>

          {/* Create Tab */}
          <TabsContent value="create">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-[#39FF14]" />
                  Generate New SOP
                </CardTitle>
                <CardDescription>
                  Provide details about your process and AI will create a professional SOP
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Process Name *</Label>
                    <Input
                      value={formData.process_name}
                      onChange={(e) => setFormData({ ...formData, process_name: e.target.value })}
                      placeholder="e.g., Client Onboarding Process"
                      data-testid="sop-process-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Department/Area</Label>
                    <Input
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      placeholder="e.g., Sales, Operations, HR"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Process Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this process does and when it's used..."
                    className="min-h-[100px]"
                    data-testid="sop-description"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Key Objectives</Label>
                    <Textarea
                      value={formData.objectives}
                      onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                      placeholder="What should this process achieve?"
                      className="min-h-[80px]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Audience</Label>
                    <Input
                      value={formData.audience}
                      onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                      placeholder="Who will follow this SOP?"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Additional Context</Label>
                  <Textarea
                    value={formData.context}
                    onChange={(e) => setFormData({ ...formData, context: e.target.value })}
                    placeholder="Any specific requirements, tools used, or special considerations..."
                    className="min-h-[80px]"
                  />
                </div>

                <Button 
                  onClick={handleGenerate} 
                  disabled={generating || !formData.process_name || !formData.description}
                  className="w-full bg-[#333333] hover:bg-[#444444] text-white h-12"
                  data-testid="generate-sop-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating SOP...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate SOP
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Library Tab */}
          <TabsContent value="library">
            {sops.length === 0 ? (
              <Card className="border-neutral-200">
                <CardContent className="p-12 text-center">
                  <ClipboardList className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">No SOPs Yet</h3>
                  <p className="text-neutral-500 mb-4">Create your first Standard Operating Procedure</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sops.map((sop) => (
                  <Card key={sop.sop_id} className="border-neutral-200 hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-[#39FF14]/10 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#39FF14]" />
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-neutral-400 hover:text-red-500"
                          onClick={() => handleDelete(sop.sop_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <h4 className="font-semibold text-neutral-900 mb-1">
                        {sop.generated_sop?.title || sop.input_data?.process_name || "Untitled SOP"}
                      </h4>
                      <p className="text-sm text-neutral-500 mb-3 line-clamp-2">
                        {sop.generated_sop?.purpose || sop.input_data?.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-400">
                          {new Date(sop.created_at).toLocaleDateString()}
                        </span>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => viewSOP(sop.sop_id)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* View Tab */}
          {selectedSOP && (
            <TabsContent value="view">
              <Card className="border-neutral-200 print:border-0 print:shadow-none">
                <CardContent className="p-8 print:p-0">
                  {/* SOP Header */}
                  <div className="border-b border-neutral-200 pb-6 mb-6 print:border-b-2 print:border-black">
                    <Badge className="bg-[#39FF14]/10 text-[#39FF14] mb-4">Standard Operating Procedure</Badge>
                    <h1 className="text-3xl font-bold text-neutral-900 mb-2">{selectedSOP.title}</h1>
                    <p className="text-neutral-600">{selectedSOP.purpose}</p>
                  </div>

                  {/* Scope */}
                  {selectedSOP.scope && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-2">Scope</h2>
                      <p className="text-neutral-600">{selectedSOP.scope}</p>
                    </div>
                  )}

                  {/* Definitions */}
                  {selectedSOP.definitions?.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-2">Definitions</h2>
                      <ul className="list-disc list-inside text-neutral-600 space-y-1">
                        {selectedSOP.definitions.map((def, idx) => (
                          <li key={idx}>{def}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Responsibilities */}
                  {selectedSOP.responsibilities?.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-3">Responsibilities</h2>
                      <div className="space-y-3">
                        {selectedSOP.responsibilities.map((resp, idx) => (
                          <div key={idx} className="bg-neutral-50 p-4 rounded-lg">
                            <h4 className="font-medium text-neutral-900 mb-2">{resp.role}</h4>
                            <ul className="list-disc list-inside text-sm text-neutral-600 space-y-1">
                              {resp.responsibilities?.map((r, i) => (
                                <li key={i}>{r}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Procedure Steps */}
                  {selectedSOP.procedure?.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-3">Procedure</h2>
                      <div className="space-y-4">
                        {selectedSOP.procedure.map((step, idx) => (
                          <div key={idx} className="border border-neutral-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="w-8 h-8 bg-[#333333] text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                                {step.step_number || idx + 1}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-neutral-900 mb-1">{step.title}</h4>
                                <p className="text-neutral-600 mb-3">{step.description}</p>
                                
                                {step.substeps?.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-sm font-medium text-neutral-700 mb-1">Sub-steps:</p>
                                    <ol className="list-decimal list-inside text-sm text-neutral-600 space-y-1 ml-2">
                                      {step.substeps.map((sub, i) => (
                                        <li key={i}>{sub}</li>
                                      ))}
                                    </ol>
                                  </div>
                                )}

                                <div className="flex flex-wrap gap-2 text-xs">
                                  {step.time_estimate && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {step.time_estimate}
                                    </Badge>
                                  )}
                                  {step.tools_required?.map((tool, i) => (
                                    <Badge key={i} variant="outline" className="bg-neutral-50">
                                      {tool}
                                    </Badge>
                                  ))}
                                </div>

                                {step.tips && (
                                  <div className="mt-3 p-2 bg-amber-50 rounded text-sm text-amber-700">
                                    💡 {step.tips}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quality Checks */}
                  {selectedSOP.quality_checks?.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-2">Quality Checks</h2>
                      <ul className="space-y-2">
                        {selectedSOP.quality_checks.map((check, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{check}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Exceptions */}
                  {selectedSOP.exceptions?.length > 0 && (
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-neutral-900 mb-2">Handling Exceptions</h2>
                      <ul className="space-y-2">
                        {selectedSOP.exceptions.map((exc, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{exc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
        {selectedSOP && (
          <div className="mt-4 text-center">
            <p className="text-xs text-neutral-400">Powered by My Biz Consulting - Business Solutions Center</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SOPGeneratorPage;
